<template>
  <v-container fluid class="search-page fill-height pa-0" style=" background-color: #F9F9F9;">

    <div v-if="messages.length === 0" class="welcome-section">
      <div class="welcome-content">
        <h1 class="display-title mb-2">어떤 정보가 필요하신가요?</h1>
        <p class="subtitle mb-8">Draco와 함께 더 빠르고 정확하게 데이터를 탐색하세요.</p><br>

        <div class="search-group">
          <div class="search-box-wrapper flex-grow-1">
            <v-textarea v-model="question" placeholder="궁금하신 내용을 입력해주세요!" variant="plain" auto-grow rows="1"
              max-rows="5" maxlength="5000" @keyup.enter.exact.prevent="startChat" class="premium-input" hide-details>
              <template v-slot:append-inner>
                <v-btn icon="mdi-arrow-up" :color="(!question || !question.trim()) ? 'grey' : 'primary'" size="small"
                  elevation="2" @click="startChat" :loading="loading" :disabled="!question || !question.trim()"
                  rounded="lg"></v-btn>
              </template>
            </v-textarea>
          </div>
          <div class="d-flex flex-column ml-4 source-selection" tooltip="조회할 출처를 선택합니다.">
            <v-checkbox v-model="dbTool" label="DB" density="compact" hide-details color="primary"
              :disabled="dbTool && !docTool && !lawTool"></v-checkbox>
            <v-checkbox v-model="docTool" label="문서" density="compact" hide-details color="primary"
              :disabled="docTool && !dbTool && !lawTool"></v-checkbox>
            <v-checkbox v-model="lawTool" label="법령" density="compact" hide-details color="primary"
              :disabled="lawTool && !dbTool && !docTool"></v-checkbox>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="chat-interface" style=" background-color: #F9F9F9;">
      <div class="messages-container" ref="messageArea">
        <div v-for="(msg, index) in messages" :key="index" :class="['msg-wrapper', msg.role]">
          <div class="msg-bubble">
            <div class="msg-meta" :class="{ 'msg-meta--assistant': msg.role === 'assistant' }">
              <template v-if="msg.role === 'assistant'">
                <div class="answer-heading">
                  <div class="answer-heading__info">
                    <div class="answer-heading__identity">
                      <span class="answer-heading__avatar"><v-icon size="14" aria-hidden="true">mdi-robot</v-icon></span>
                      <span class="answer-heading__name">Draco AI</span>
                    </div>
                    <AnswerModelInfo :llm="msg.llm" />
                    <span v-if="msg.crtDt && msg.endTd" class="answer-response-time" title="수행시간"
                      :aria-label="`수행시간 ${formatResponseTime(msg.crtDt, msg.endTd)}`">
                      <v-icon size="14" aria-hidden="true">mdi-clock-outline</v-icon>
                      {{ formatResponseTime(msg.crtDt, msg.endTd) }}
                    </span>
                  </div>
                  <v-btn size="x-small" variant="text" density="compact" class="answer-collapse-btn"
                    :prepend-icon="msg.isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up'"
                    :aria-label="msg.isCollapsed ? '답변 펼치기' : '답변 접기'" @click="toggleCollapse(msg)">
                    {{ msg.isCollapsed ? '펴기' : '접기' }}
                  </v-btn>
                </div>
              </template>
              <template v-else>
                <v-icon icon="mdi-account" size="x-small"></v-icon>
                <span class="text-caption ml-1">나</span>
                <v-spacer></v-spacer>
                <v-btn icon="mdi-content-copy" size="x-small" variant="text" density="compact"
                  @click="copyToClipboard(msg.text)" title="질문 복사" class="ml-2"></v-btn>
              </template>
            </div>

            <template v-if="msg.role === 'assistant'">
              <v-expand-transition>
                <div v-show="!msg.isCollapsed">
                  <div class="msg-content-wrapper">
                    <div v-if="msg.previewCode" class="msg-tabs mb-2">
                      <v-btn-toggle v-model="msg.activeTab" mandatory density="compact" variant="tonal" color="primary"
                        rounded="lg">
                        <v-btn value="preview" size="x-small" prepend-icon="mdi-play">결과 보기</v-btn>
                        <v-btn value="text" size="x-small">코드/텍스트 보기</v-btn>
                      </v-btn-toggle>
                    </div>

                    <div class="msg-text" v-html="renderMarkdown(msg.text, msg.activeTab === 'preview')"></div>

                    <div v-if="msg.previewCode && msg.activeTab === 'preview'"
                      class="preview-container mt-2">
                      <iframe :srcdoc="getSrcDoc(msg.previewCode, 'frame-' + index)" sandbox="allow-scripts"
                        class="preview-iframe" :data-frame-id="'frame-' + index" referrerpolicy="no-referrer"
                        allow="camera 'none'; microphone 'none'; geolocation 'none'; payment 'none'; usb 'none'"
                        :title="`그래프 미리보기 ${index + 1}`"
                        :style="{ height: (iframeHeights['frame-' + index] || 400) + 'px' }"></iframe>
                    </div>
                  </div>
                  <div class="msg-footer mt-2 d-flex align-center">
                    <v-btn v-if="msg.source" variant="outlined" size="x-small" prepend-icon="mdi-text-search"
                      @click="showSource(msg)" class="source-btn mr-2" :loading="sourceLoading && selectedMsgSeq === msg.seq">
                      출처보기
                    </v-btn>
                    <v-spacer></v-spacer>
                    <div class="feedback-actions">
                      <v-btn :color="msg.feedback === 1 ? 'success' : ''" variant="text" size="small" class="feedback-btn"
                        @click="setFeedback(msg, 1)" :class="{ 'active': msg.feedback === 1 }" title="좋아요">
                        <v-icon size="28">{{ msg.feedback === 1 ? 'mdi-thumb-up' : 'mdi-thumb-up-outline' }}</v-icon>
                      </v-btn>

                      <v-btn :color="msg.feedback === 0 ? 'amber' : ''" variant="text" size="small" class="feedback-btn"
                        @click="setFeedback(msg, 0)" :class="{ 'active': msg.feedback === 0 }" title="보통이에요">
                        <v-icon size="28">{{ msg.feedback === 0 ? 'mdi-minus-circle' : 'mdi-minus-circle-outline' }}</v-icon>
                      </v-btn>

                      <v-btn :color="msg.feedback === -1 ? 'error' : ''" variant="text" size="small" class="feedback-btn"
                        @click="setFeedback(msg, -1)" :class="{ 'active': msg.feedback === -1 }" title="별로에요">
                        <v-icon size="28">{{ msg.feedback === -1 ? 'mdi-thumb-down' : 'mdi-thumb-down-outline' }}</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-expand-transition>
              <div v-if="msg.isCollapsed" class="collapsed-placeholder py-1 px-2 text-caption text-grey-darken-1 cursor-pointer d-flex align-center" @click="msg.isCollapsed = false">
                <v-icon size="x-small" color="primary" class="mr-1">mdi-chevron-down</v-icon>
                <span>답변이 접혀 있습니다. (클릭하여 펼치기)</span>
              </div>
            </template>
            <template v-else>
              <div class="msg-content-wrapper">
                <div class="msg-text" v-html="renderMarkdown(msg.text)"></div>
              </div>
            </template>
          </div>
        </div>

        <div v-if="loading" class="msg-wrapper assistant">
          <div class="msg-bubble loading-bubble">
            <div class="processing-status mb-2" v-if="processingStage">
              <v-icon size="x-small" color="primary" class="mr-1">mdi-cached</v-icon>
              <span class="text-caption font-weight-medium">{{ processingStage }}</span>
            </div>
            <div class="typing-indicator">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      </div>

      <div class="input-container pa-4 position-relative">
        <div class="search-group bottom">
          <div class="search-box-wrapper flex-grow-1">
            <v-textarea v-model="question" placeholder="추가 질문을 입력하세요..." variant="plain" auto-grow rows="1" max-rows="5"
              maxlength="5000" @keyup.enter.exact.prevent="startChat" class="premium-input" hide-details
              :disabled="loading">
              <template v-slot:append-inner>
                <v-btn icon="mdi-arrow-up" :color="(!question || !question.trim()) ? 'grey' : 'primary'" size="small"
                  @click="startChat" :loading="loading" :disabled="!question || !question.trim()" rounded="lg"></v-btn>
              </template>
            </v-textarea>
          </div>
          <div class="d-flex flex-column ml-4 source-selection">
            <v-checkbox v-model="dbTool" label="DB" density="compact" hide-details color="primary"
              :disabled="dbTool && !docTool && !lawTool"></v-checkbox>
            <v-checkbox v-model="docTool" label="문서" density="compact" hide-details color="primary"
              :disabled="docTool && !dbTool && !lawTool"></v-checkbox>
            <v-checkbox v-model="lawTool" label="법령" density="compact" hide-details color="primary"
              :disabled="lawTool && !dbTool && !docTool"></v-checkbox>
          </div>
        </div>

        <div v-if="messages.length > 0" class="collapse-btn-wrapper">
          <v-btn
            variant="outlined"
            density="compact"
            rounded="md"
            class="square-box-btn subtle-collapse-box-btn"
            @click="toggleAllCollapse"
            :title="areAllCollapsed ? '답변 펴기' : '답변 접기'"
          >
            <div class="d-flex flex-column align-center justify-center lh-1">
              <span>답변</span>
              <span>{{ areAllCollapsed ? '펴기' : '접기' }}</span>
            </div>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Source Dialog -->
    <v-dialog v-model="sourceDialog" width="70vw" scrollable>
      <v-card class="source-dialog-card">
        <v-card-title class="d-flex align-center pa-4 border-bottom">
          <v-icon icon="mdi-text-search" color="primary" class="mr-2"></v-icon>
          <span class="text-h6 font-weight-bold">검색 출처 정보</span>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" size="small" @click="sourceDialog = false"></v-btn>
        </v-card-title>
        <v-card-text class="pa-4 bg-light">
          <div v-if="selectedSource">
            <template v-for="(sourceItem, idx) in selectedSource" :key="idx">
              <v-alert v-if="sourceItem.error" type="error" variant="tonal" density="compact" class="mb-4">
                오류 발생: {{ sourceItem.error }}
              </v-alert>
              <div v-if="sourceItem.type === 'SQL'" class="source-section mb-6">
                <div class="source-label mb-2"><v-icon size="small" class="mr-1">mdi-table</v-icon>조회 데이터 결과</div>
                <div class="source-table-wrapper" v-html="renderSourceTable(sourceItem.data)"></div>
                <br>
                <div v-if="sourceItem.expln" class="expln-section mb-4">
                  <div class="source-label mb-2"><v-icon size="small" class="mr-1">mdi-comment-text-outline</v-icon>질문 의도
                    및 설명
                  </div>
                  <div class="expln-text pa-3 rounded bg-blue-lighten-5 text-body-2 border-left-blue">
                    {{ sourceItem.expln }}
                  </div>
                </div>
                <div class="source-label mb-2"><v-icon size="small" class="mr-1">mdi-database-search</v-icon>실행된 SQL</div>
                <pre class="sql-code pa-3 rounded mb-4">{{ sourceItem.sql }}</pre>

              </div>
              <div v-else-if="sourceItem.type === 'DOC'" class="source-section mb-6">
                <div class="source-label mb-2"><v-icon size="small" class="mr-1">mdi-file-document-outline</v-icon>문서 검색
                  결과
                </div>
                <div v-for="(doc, i) in sourceItem.data" :key="i" class="doc-item pa-3 rounded mb-3 border">
                  <div class="doc-header d-flex align-center mb-1">
                    <div class="doc-title font-weight-bold">[문서: {{ doc.title }}]</div>
                    <v-spacer></v-spacer>
                    <div v-if="doc.page_num !== undefined" class="doc-page text-caption mr-2">Page: {{ doc.page_num + 1 }}
                    </div>
                    <v-btn size="x-small" color="primary" variant="tonal" prepend-icon="mdi-open-in-new"
                      @click="openPdf(doc)">
                      문서 열기
                    </v-btn>
                  </div>
                  <div class="doc-content text-body-2" v-html="renderMarkdown(doc.content)"></div>
                </div>
              </div>
              <div v-else-if="sourceItem.type === 'LAW'" class="source-section mb-6">
                <div class="source-label mb-2"><v-icon size="small" class="mr-1">mdi-gavel</v-icon>법령 검색 결과</div>
                <div v-for="(doc, i) in sourceItem.data" :key="i" class="doc-item pa-3 rounded mb-3 border">
                  <div class="doc-header d-flex align-center mb-1">
                    <div class="doc-title font-weight-bold text-primary">{{ doc.title }}</div>
                  </div>
                  <div class="doc-content law-doc-content text-body-2 pt-2" v-html="renderMarkdown(doc.content)"></div>
                </div>
              </div>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="copySnackbar" :timeout="1000" location="center" color="success">
      <div class="text-center font-weight-bold">{{ copyMessage }}</div>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import axios from '@/plugins/axios';
import { getChatRequestSettings } from '@/utils/modelSettings';
import AnswerModelInfo from './AnswerModelInfo.vue';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import markedKatex from 'marked-katex-extension';
import 'katex/dist/katex.min.css';

marked.use(markedKatex({
  throwOnError: false
}));

// Helper to extract HTML/JS/CSS code blocks for preview
const extractPreviewCode = (text) => {
  if (!text) return null;
  const tokens = marked.lexer(text);
  const result = { html: '', js: '', css: '' };
  let hasCode = false;

  tokens.forEach(token => {
    if (token.type === 'code') {
      const lang = (token.lang || '').toLowerCase();
      if (lang === 'html') {
        result.html += token.text + '\n';
        hasCode = true;
      } else if (lang === 'javascript' || lang === 'js') {
        result.js += token.text + '\n';
        hasCode = true;
      } else if (lang === 'css') {
        result.css += token.text + '\n';
        hasCode = true;
      }
    }
  });

  return hasCode ? result : null;
};

const formatResponseTime = (start, end) => {
  if (!start || !end) return '-';
  const startTime = new Date(start);
  const endTime = new Date(end);
  const diffMs = endTime.getTime() - startTime.getTime();
  if (diffMs < 0) return '-';
  return (diffMs / 1000).toFixed(1) + '초';
};

const GRAPH_IFRAME_CSP = [
  "default-src 'none'",
  "script-src 'unsafe-inline' https://cdn.jsdelivr.net https://cdnjs.cloudflare.com",
  "style-src 'unsafe-inline'",
  'img-src data: blob:',
  'font-src data:',
  "connect-src 'none'",
  "object-src 'none'",
  "base-uri 'none'",
  "form-action 'none'"
].join('; ');

const MARKDOWN_SANITIZE_CONFIG = {
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'base', 'meta', 'link'],
  FORBID_ATTR: ['srcdoc']
};

const sanitizeMarkdownHtml = (html) => DOMPurify.sanitize(html, MARKDOWN_SANITIZE_CONFIG);

const getSrcDoc = (code, id) => {
  if (!code) return '';
  const frameId = JSON.stringify(String(id));
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <meta http-equiv="Content-Security-Policy" content="${GRAPH_IFRAME_CSP}">
        <meta name="referrer" content="no-referrer">
        <style>
          body { font-family: sans-serif; margin: 0; padding: 16px; overflow: hidden; }
          ${code.css}
          *, *::before, *::after { box-sizing: border-box; }
          html, body { width: 100% !important; max-width: 100% !important; }
          #content-wrapper { width: 100% !important; max-width: 100% !important; }
          #content-wrapper > * { width: 100% !important; max-width: 100% !important; }
          #content-wrapper canvas,
          #content-wrapper svg { width: 100% !important; max-width: 100% !important; }
        </style>
      </head>
      <body>
        <div id="content-wrapper">
          ${code.html}
        </div>
        <script>${code.js}</` + `script>
        <script>
          const sendHeight = () => {
            const height = document.getElementById('content-wrapper').offsetHeight + 32; // padding
            window.parent.postMessage({ type: 'setHeight', id: ${frameId}, height: height }, '*');
          };
          window.onload = sendHeight;
          new ResizeObserver(sendHeight).observe(document.getElementById('content-wrapper'));
        </` + `script>
      </body>
    </html>
  `;
};

// Custom renderer logic removed to ensure code is visible in Text view
// marked.use(markedKatex) is already called above

const route = useRoute();
const question = ref('');
const messages = ref([]);
const loading = ref(false);
const chatSessSeq = ref(null);
const messageArea = ref(null);
const sourceDialog = ref(false);
const selectedSource = ref(null);
const sourceLoading = ref(false);
const selectedMsgSeq = ref(null);
const processingStage = ref('');

const copySnackbar = ref(false);
const copyMessage = ref('');

// Persistence for selected tools
const dbTool = ref(localStorage.getItem('dbTool') === null ? true : localStorage.getItem('dbTool') === 'true');
const docTool = ref(localStorage.getItem('docTool') === null ? true : localStorage.getItem('docTool') === 'true');
const lawTool = ref(localStorage.getItem('lawTool') === null ? true : localStorage.getItem('lawTool') === 'true');

// Watch for changes and save to localStorage
watch(dbTool, (newVal) => localStorage.setItem('dbTool', newVal));
watch(docTool, (newVal) => localStorage.setItem('docTool', newVal));
watch(lawTool, (newVal) => localStorage.setItem('lawTool', newVal));

const iframeHeights = ref({});

const handleIframeMessage = (event) => {
  if (!event.data || event.data.type !== 'setHeight' || typeof event.data.id !== 'string') return;

  const sourceIframe = Array.from(document.querySelectorAll('iframe.preview-iframe'))
    .find((iframe) => iframe.dataset.frameId === event.data.id);
  if (!sourceIframe || event.source !== sourceIframe.contentWindow) return;

  const requestedHeight = Number(event.data.height);
  if (!Number.isFinite(requestedHeight)) return;
  iframeHeights.value[event.data.id] = Math.min(Math.max(requestedHeight, 160), 2000);
};


const fetchSessionHistory = async (sessSeq) => {
  loading.value = true;
  try {
    const response = await axios.get(`/chatting/sessions/${sessSeq}`);
    if (response.data.status === 'success') {
      messages.value = [];
      response.data.data.history.forEach(item => {
        messages.value.push({ role: 'user', text: item.chatQstn });
        let sourceObj = null;
        if (item.chatSource) {
          try {
            sourceObj = typeof item.chatSource === 'string' ? JSON.parse(item.chatSource) : item.chatSource;
          } catch (e) { console.warn('Failed to parse chatSource', e); }
        }
        messages.value.push({
          role: 'assistant',
          text: item.chatAns,
          source: sourceObj,
          seq: item.chatQstnSeq,
          feedback: item.feedback,
          llm: item.llm,
          crtDt: item.crtDt,
          endTd: item.endTd,
          previewCode: extractPreviewCode(item.chatAns),
          activeTab: extractPreviewCode(item.chatAns) ? 'preview' : 'text',
          isCollapsed: false
        });
      });
      chatSessSeq.value = sessSeq;
      await scrollToBottom();
    }
  } catch (error) {
    console.error('Failed to fetch history:', error);
  } finally {
    loading.value = false;
  }
};

watch(() => route.query.sess, (newSess) => {
  if (newSess) {
    fetchSessionHistory(newSess);
  } else {
    clearContent();
  }
});

const clearContent = () => {
  messages.value = [];
  question.value = '';
  chatSessSeq.value = null;
  processingStage.value = '';
};

onMounted(() => {
  document.documentElement.style.overflowY = 'hidden';
  window.addEventListener('clear-search-content', clearContent);
  window.addEventListener('message', handleIframeMessage);
  if (route.query.q) {
    question.value = route.query.q;
    startChat();
  }
  if (route.query.sess) {
    fetchSessionHistory(route.query.sess);
  }
});

onUnmounted(() => {
  document.documentElement.style.overflowY = '';
  window.removeEventListener('clear-search-content', clearContent);
  window.removeEventListener('message', handleIframeMessage);
});

const renderMarkdown = (text, hideCode = false) => {
  if (!text) return '';

  let processedText = String(text);

  // Handle common LaTeX delimiters
  processedText = processedText.replace(/\\\[([\s\S]*?)\\\]/g, '$$$$$1$$$$');
  processedText = processedText.replace(/\\\(([\s\S]*?)\\\)/g, '$$$1$$');
  const safeText = processedText.replace(/~/g, '&#126;');

  if (hideCode) {
    const localRenderer = new marked.Renderer();
    const originalCode = localRenderer.code.bind(localRenderer);
    localRenderer.code = (token) => {
      const lang = (token.lang || '').toLowerCase();
      if (['html', 'javascript', 'js', 'css'].includes(lang)) {
        return ''; // Hide code blocks that are being previewed
      }
      return originalCode(token);
    };
    return sanitizeMarkdownHtml(marked.parse(safeText, { renderer: localRenderer }));
  }

  return sanitizeMarkdownHtml(marked.parse(safeText));
};

const showSource = async (msg) => {
  if (!msg.seq) {
    selectedSource.value = msg.source;
    sourceDialog.value = true;
    return;
  }

  selectedMsgSeq.value = msg.seq;
  sourceLoading.value = true;
  try {
    const response = await axios.get(`/chatting/questions/${msg.seq}/source`);
    if (response.data.status === 'success') {
      const respData = response.data.data;
      if (respData && respData.type === 'MULTI') {
        selectedSource.value = respData.sources || [];
      } else {
        selectedSource.value = respData ? [respData] : [];
      }
      sourceDialog.value = true;
    }
  } catch (error) {
    console.error('Failed to fetch source details:', error);
  } finally {
    sourceLoading.value = false;
  }
};

const renderSourceTable = (data) => {
  if (!data) return '데이터가 없습니다.';
  if (typeof data === 'string') return renderMarkdown(data);
  if (data.length === 0) return '데이터가 없습니다.';

  const headers = Object.keys(data[0]);
  let md = '| ' + headers.join(' | ') + ' |\n';
  md += '| ' + headers.map(() => '---').join(' | ') + ' |\n';

  data.forEach(row => {
    md += '| ' + headers.map(h => {
      const val = row[h];
      return val !== null && val !== undefined ? String(val).replace(/\n/g, ' ') : '';
    }).join(' | ') + ' |\n';
  });

  return renderMarkdown(md);
};

const openPdf = (doc) => {
  const baseUrl = axios.defaults.baseURL;

  let viewUrl = baseUrl.endsWith('/')
    ? `${baseUrl}rag/docs/view/${encodeURIComponent(doc.title)}`
    : `${baseUrl}/rag/docs/view/${encodeURIComponent(doc.title)}`;

  // PDF page parameter (1-indexed for most browsers/PDF viewers)
  const pageParam = doc.page_num !== undefined ? `#page=${doc.page_num + 1}` : '';
  window.open(viewUrl + pageParam, '_blank');
};

const scrollToBottom = async () => {
  await nextTick();
  if (messageArea.value) {
    messageArea.value.scrollTop = messageArea.value.scrollHeight;
  }
};

const startChat = async () => {
  if (!question.value.trim() || loading.value) return;

  const userQuestion = question.value;

  messages.value.push({ role: 'user', text: userQuestion }); // Show original question to user
  question.value = '';
  loading.value = true;
  processingStage.value = '';
  await scrollToBottom();

  try {
    const modelSettings = await getChatRequestSettings(axios);
    const payload = {
      question: userQuestion,
      chatSessSeq: chatSessSeq.value,
      ...modelSettings,
      dbTool: dbTool.value,
      docTool: docTool.value,
      lawTool: lawTool.value
    };

    const baseUrl = axios.defaults.baseURL;
    const url = baseUrl.endsWith('/') ? `${baseUrl}chatting` : `${baseUrl}/chatting`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Split by double newline (SSE event separator)
      const parts = buffer.split(/\n\n/);
      // Keep the last potentially incomplete part in the buffer
      buffer = parts.pop();

      for (const part of parts) {
        if (!part.trim()) continue;

        const lines = part.split(/\n/);
        let eventType = '';
        let dataStr = '';

        for (const line of lines) {
          if (line.startsWith('event:')) eventType = line.substring(6).trim();
          else if (line.startsWith('data:')) dataStr = line.substring(5).trim();
        }

        if (eventType === 'progress') {
          processingStage.value = dataStr;
        } else if (eventType === 'result') {
          const res = JSON.parse(dataStr);
          messages.value.push({
            role: 'assistant',
            text: res.answer,
            source: res.chatSource,
            seq: res.chatQstnSeq,
            feedback: null,
            llm: res.llm,
            crtDt: res.crtDt,
            endTd: res.endTd,
            previewCode: extractPreviewCode(res.answer),
            activeTab: extractPreviewCode(res.answer) ? 'preview' : 'text',
            isCollapsed: false
          });
          chatSessSeq.value = res.chatSessSeq;
          window.dispatchEvent(new CustomEvent('refresh-chat-sessions'));
        } else if (eventType === 'error') {
          console.error('SSE error:', dataStr);
          const err = JSON.parse(dataStr);
          messages.value.push({ role: 'assistant', text: '오류가 발생했습니다: ' + (err.message || err), isCollapsed: false });
        } else if (eventType === 'connected') {
          console.log('SSE Connected');
        }
      }
    }
  } catch (error) {
    console.error('Chat error:', error);
    messages.value.push({ role: 'assistant', text: '서버와 통신 중 오류가 발생했습니다.', isCollapsed: false });
  } finally {
    loading.value = false;
    processingStage.value = '';
    await scrollToBottom();
  }
};

const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers or non-secure contexts (HTTP instead of HTTPS)
      const textArea = document.createElement("textarea");
      textArea.value = text;
      // Move textarea out of the viewport so it's not visible
      textArea.style.position = "absolute";
      textArea.style.left = "-999999px";
      document.body.prepend(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (error) {
        console.error('Fallback copy error:', error);
        throw new Error('Fallback copy error');
      } finally {
        textArea.remove();
      }
    }
    copyMessage.value = '질문이 복사되었습니다.';
    copySnackbar.value = true;
  } catch (err) {
    console.error('복사에 실패했습니다:', err);
    copyMessage.value = '질문 복사에 실패했습니다.';
    copySnackbar.value = true;
  }
};

const setFeedback = async (msg, value) => {
  if (!msg.seq) return;

  const originalFeedback = msg.feedback;
  // Toggle if clicked same
  const newValue = msg.feedback === value ? null : value;
  msg.feedback = newValue;

  try {
    const response = await axios.post(`/chatting/questions/${msg.seq}/feedback`, {
      feedback: newValue
    });
    if (response.data.status !== 'success') {
      msg.feedback = originalFeedback;
    }
  } catch (error) {
    console.error('Failed to set feedback:', error);
    msg.feedback = originalFeedback;
  }
};

const toggleCollapse = (msg) => {
  if (msg) {
    msg.isCollapsed = !msg.isCollapsed;
  }
};

const areAllCollapsed = computed(() => {
  const assistantMsgs = messages.value.filter(m => m.role === 'assistant');
  if (assistantMsgs.length === 0) return false;
  return assistantMsgs.every(m => m.isCollapsed);
});

const toggleAllCollapse = () => {
  const targetState = !areAllCollapsed.value;
  messages.value.forEach(m => {
    if (m.role === 'assistant') {
      m.isCollapsed = targetState;
    }
  });
};

</script>

<style scoped>
.search-page {
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
  color: #1a1a1a;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Glow Background */
.bg-glow {
  position: absolute;
  width: 600px;
  height: 600px;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.15;
  z-index: 0;
  pointer-events: none;
}

.bg-glow-1 {
  background: radial-gradient(circle, #4285f4, transparent);
  top: -200px;
  right: -200px;
}

.bg-glow-2 {
  background: radial-gradient(circle, #34a853, transparent);
  bottom: -200px;
  left: -200px;
}

.welcome-content {
  width: 100%;
  max-width: 1000px !important;
  margin: 0 auto;
}

.welcome-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
  text-align: center;
  padding: 0 40px;
}

.display-title {
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -1px;
}

.subtitle {
  color: #666;
  font-size: 1.1rem;
}

.search-box-wrapper {
  width: 100% !important;
  max-width: 1000px !important;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 24px;
  padding: 12px 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
}

.search-box-wrapper:focus-within {
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border-color: rgba(66, 133, 244, 0.3);
}

.search-group {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

.search-group.bottom {
  max-width: 1100px;
  padding: 0 40px;
}

.premium-input :deep(textarea) {
  padding: 24px 0 12px 0;
  font-size: 1.15rem;
  line-height: 1.6;
  color: #1a1a1a;
  text-rendering: optimizeLegibility !important;
  overflow: visible !important;
}

.source-selection {
  min-width: 80px;
}

.source-selection :deep(.v-checkbox .v-label) {
  font-size: 0.75rem;
  margin-inline-start: 4px;
  white-space: nowrap;
}

.source-selection :deep(.v-selection-control) {
  min-height: 24px;
}

.source-selection :deep(.v-selection-control__wrapper) {
  height: 24px;
  width: 24px;
}

.source-selection :deep(.v-checkbox .v-selection-control) {
  padding-top: 0;
  padding-bottom: 0;
}



/* Chat Interface */
.chat-interface {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  z-index: 1;
  background: rgba(255, 255, 255, 0.8);
}

.chat-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.messages-container {
  flex-grow: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  scroll-behavior: smooth;
}

.msg-wrapper {
  display: flex;
  width: 100%;
}

.msg-bubble {
  max-width: 85%;
  padding: 1rem 1.25rem;
  border-radius: 18px;
  position: relative;
}

.user {
  justify-content: flex-end;
}

.user .msg-bubble {
  background-color: #f0f2f5;
  color: #1a1a1a;
  border-bottom-right-radius: 4px;
}

.assistant .msg-bubble {
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
  border-bottom-left-radius: 4px;
  width: 66.66%;
}

.msg-meta {
  color: #888;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 4px;
  margin-bottom: 4px;
}

.msg-meta--assistant {
    width: 100%;
    margin-bottom: 10px;
    color: #6b7280;
}

.answer-heading {
  display: flex;
  align-items: center;
    gap: 12px;
  width: 100%;
    min-width: 0;
  }

  .answer-heading__info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: thin;
    white-space: nowrap;
  }

  .answer-heading__info > * {
    flex-shrink: 0;
}

.answer-heading__identity {
  display: inline-flex;
  align-items: center;
    gap: 5px;
}

.answer-heading__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
    width: 20px;
    height: 20px;
    color: #6b7280;
}

.answer-heading__name {
    color: #6b7280;
    font-size: 12px;
    font-weight: 500;
  letter-spacing: -0.2px;
}

.answer-response-time {
  display: inline-flex;
  align-items: center;
  gap: 5px;
    padding-left: 10px;
    border-left: 1px solid #e5e7eb;
    color: #6b7280;
  font-size: 12px;
    font-weight: 400;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
  white-space: nowrap;
}

.answer-collapse-btn {
    flex-shrink: 0;
    min-height: 24px;
  border-radius: 8px;
  color: #64748b;
  font-size: 12px;
  letter-spacing: 0;
  text-transform: none;
}

.msg-text {
  font-size: 1rem;
  line-height: 1.6;
}

.preview-container {
  width: 100%;
  max-width: none;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  overflow: hidden;
  background: white;
}

.preview-iframe {
  width: 100%;
  max-width: none;
  box-sizing: border-box;
  border: none;
  display: block;
}

.msg-tabs {
  display: flex;
  justify-content: flex-start;
}

.msg-text :deep(p) {
  margin-bottom: 0.8rem;
}

.msg-text :deep(p:last-child) {
  margin-bottom: 0;
}

.msg-text :deep(h1),
.msg-text :deep(h2),
.msg-text :deep(h3) {
  margin-top: 1.2rem;
  margin-bottom: 0.8rem;
  font-weight: 700;
}

.msg-text :deep(h1) {
  font-size: 1.5rem;
}

.msg-text :deep(h2) {
  font-size: 1.3rem;
}

.msg-text :deep(h3) {
  font-size: 1.1rem;
}

.msg-text :deep(ul),
.msg-text :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.msg-text :deep(li) {
  margin-bottom: 0.4rem;
}

.msg-text :deep(blockquote) {
  border-left: 4px solid #ddd;
  padding-left: 1rem;
  margin: 1rem 0;
  color: #666;
  font-style: italic;
}

.msg-text :deep(pre) {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  margin: 1rem 0;
  font-size: 0.9rem;
  border: 1px solid #eee;
}

.msg-text :deep(code) {
  background: #f1f3f5;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: 'Fira Code', monospace;
  font-size: 0.9em;
}

.msg-text :deep(pre code) {
  background: transparent;
  padding: 0;
  border-radius: 0;
  white-space: pre;
}

.msg-text :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
  font-size: 0.9rem;
}

.msg-text :deep(th),
.msg-text :deep(td) {
  border: 1px solid #eee;
  padding: 8px 12px;
  text-align: left;
}

.msg-text :deep(th) {
  background: #f8f9fa;
  font-weight: 600;
}

.source-btn {
  font-size: 0.7rem !important;
  color: #4285f4;
  border-color: rgba(66, 133, 244, 0.2);
  height: 24px;
}

.source-btn:hover {
  background-color: rgba(66, 133, 244, 0.05);
  border-color: #4285f4;
}

.feedback-actions {
  display: flex;
  gap: 4px;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.msg-bubble:hover .feedback-actions {
  opacity: 1;
}

.feedback-btn {
  opacity: 0.7;
  width: 36px !important;
  height: 36px !important;
  min-width: 0 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.feedback-actions {
  display: flex !important;
  gap: 8px;
  opacity: 0.6;
  transition: opacity 0.2s;
  align-items: center;
}

.feedback-btn.active {
  opacity: 1;
}

.feedback-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.source-dialog-card {
  border-radius: 16px;
  overflow: hidden;
}

.bg-light {
  background-color: #f8f9fa;
}

.source-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
}

.sql-code {
  background: #2d2d2d;
  color: #f1f1f1;
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  overflow-x: auto;
}

.source-table-wrapper :deep(table) {
  width: 100%;
  border-collapse: collapse;
  background: white;
  font-size: 0.9rem;
}

.source-table-wrapper :deep(th),
.source-table-wrapper :deep(td) {
  border: 1px solid #ddd;
  padding: 8px 12px;
  text-align: left;
}

.source-table-wrapper :deep(th) {
  background: #f1f3f5;
  font-weight: 600;
}

.doc-item {
  background: white;
  border-color: #eee !important;
}

.doc-title {
  color: #4285f4;
  font-size: 0.95rem;
}

.doc-content {
  color: #444;
  line-height: 1.5;
}

.law-doc-content :deep(ol) {
  margin-left: 25px;
}

.expln-text {
  line-height: 1.6;
  white-space: pre-wrap;
}

.border-left-blue {
  border-left: 4px solid #4285f4;
}

.input-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background: transparent;
}

.collapse-btn-wrapper {
  position: absolute;
  right: 32px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
}

/* Square Box Button Base */
.square-box-btn {
  width: 46px !important;
  height: 46px !important;
  min-width: 46px !important;
  min-height: 46px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 8px !important;
  font-size: 0.68rem !important;
  font-weight: 600 !important;
  line-height: 1.15 !important;
  text-align: center !important;
  border-color: rgba(0, 0, 0, 0.15) !important;
  background-color: rgba(255, 255, 255, 0.9) !important;
  color: #555555 !important;
  transition: all 0.2s ease;
}

.lh-1 {
  line-height: 1.15 !important;
}

.square-box-btn:hover {
  border-color: #4285f4 !important;
  color: #4285f4 !important;
  background-color: #ffffff !important;
}

/* Subtle Collapse Control Box Button */
.subtle-collapse-box-btn {
  border-color: rgba(0, 0, 0, 0.15) !important;
  color: #666666 !important;
  background-color: rgba(255, 255, 255, 0.8) !important;
  font-size: 0.65rem !important;
  font-weight: 500;
  text-transform: none;
  box-shadow: none !important;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.subtle-collapse-box-btn:hover {
  border-color: rgba(66, 133, 244, 0.4) !important;
  color: #4285f4 !important;
  background-color: #ffffff !important;
}

.processing-status {
  display: flex;
  align-items: center;
  color: #4285f4;
  animation: fadeIn 0.3s ease-out;
}

.processing-status :deep(.v-icon) {
  animation: spinner 2s linear infinite;
}

@keyframes spinner {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Typing Indicator */
.typing-indicator span {
  height: 8px;
  width: 8px;
  float: left;
  margin: 0 1px;
  background-color: #9E9E9E;
  display: block;
  border-radius: 50%;
  opacity: 0.4;
}

.typing-indicator span:nth-of-type(1) {
  animation: 1s blink infinite 0.3333s;
}

.typing-indicator span:nth-of-type(2) {
  animation: 1s blink infinite 0.6666s;
}

.typing-indicator span:nth-of-type(3) {
  animation: 1s blink infinite 0.9999s;
}

@keyframes blink {
  50% {
    opacity: 1;
  }
}

@media (max-width: 600px) {
  .display-title {
    font-size: 1.8rem;
  }

  .msg-bubble {
    max-width: 95%;
    width: auto !important;
  }
}

.collapsed-placeholder {
  user-select: none;
  background-color: rgba(66, 133, 244, 0.05);
  border-radius: 8px;
  margin-top: 4px;
  transition: background-color 0.2s;
}

.collapsed-placeholder:hover {
  background-color: rgba(66, 133, 244, 0.12);
}
</style>
